import { useEffect, useState, useCallback } from "react";
import { Pagination } from "@/components/Pagination";
import { usePaged } from "@/lib/usePaged";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import MaintenanceWindowCard from "@/components/MaintenanceWindowCard";
import SecurityProofDashboard from "@/components/SecurityProofDashboard";
import { ShieldAlert, LogIn, KeyRound, PackageCheck, AlertTriangle, PlaneTakeoff, MapPin, Save, Loader2, SlidersHorizontal, Users, Wrench, MonitorSmartphone, Lock } from "lucide-react";

const TYPE_META = {
  login: { label: "Giriş", Icon: LogIn, cls: "bg-sky-100 text-sky-700" },
  step_up: { label: "Kimlik Doğrulama", Icon: KeyRound, cls: "bg-violet-100 text-violet-700" },
  critical_submit: { label: "Kritik İşlem", Icon: PackageCheck, cls: "bg-emerald-100 text-emerald-700" },
  impossible_travel: { label: "İmkânsız Seyahat", Icon: PlaneTakeoff, cls: "bg-red-100 text-red-700" },
  new_device: { label: "Yeni Cihaz", Icon: MonitorSmartphone, cls: "bg-amber-100 text-amber-700" },
  account_locked: { label: "Hesap Kilidi", Icon: Lock, cls: "bg-rose-100 text-rose-700" },
};
const METHOD_LABEL = { password: "Şifre", pin: "PIN", card: "Kart" };
const fmtDT = (iso) => {
  try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "medium" }); }
  catch { return iso || ""; }
};
const osmEmbed = (lat, lon) =>
  `https://www.openstreetmap.org/export/embed.html?bbox=${lon - 0.08}%2C${lat - 0.05}%2C${lon + 0.08}%2C${lat + 0.05}&layer=mapnik&marker=${lat}%2C${lon}`;

export default function Security() {
  const [rows, setRows] = useState([]);
  const secPaged = usePaged(rows, 20);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [onlyFail, setOnlyFail] = useState(false);
  const [q, setQ] = useState("");
  const [mapRow, setMapRow] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [alertFreq, setAlertFreq] = useState("daily");
  const [newDevices, setNewDevices] = useState([]);
  const [savingCfg, setSavingCfg] = useState(false);
  const [sessions, setSessions] = useState(null);
  const [maint, setMaint] = useState(null);

  const loadSessions = useCallback(() => {
    api.get("/admin/active-sessions").then((r) => setSessions(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadSessions(); const t = setInterval(loadSessions, 20000); return () => clearInterval(t); }, [loadSessions]);
  useEffect(() => { api.get("/admin/maintenance").then((r) => setMaint(r.data)).catch(() => {}); }, []);
  const saveMaint = async (next) => {
    try { const { data } = await api.put("/admin/maintenance", next); setMaint(data); window.dispatchEvent(new Event("maintenance-changed")); toast.success(data.enabled ? "Bakım modu açıldı" : "Bakım modu kapatıldı"); }
    catch (e) { toast.error("Kaydedilemedi"); }
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get("/security/events", { params: { type: type || undefined, only_failures: onlyFail || undefined, q: q || undefined } })
      .then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [type, onlyFail, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/security/config").then((r) => { setCfg(r.data.impossible_travel); setAlertFreq(r.data.alert_frequency || "daily"); }).catch(() => {}); }, []);
  useEffect(() => { api.get("/security/new-devices").then((r) => setNewDevices(r.data || [])).catch(() => {}); }, []);

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const { data } = await api.put("/security/config", {
        impossible_travel_enabled: cfg.enabled,
        impossible_travel_min_km: Number(cfg.min_km) || 500,
        impossible_travel_max_kmh: Number(cfg.max_kmh) || 900,
        alert_frequency: alertFreq,
      });
      setCfg(data.impossible_travel); setAlertFreq(data.alert_frequency || "daily");
      toast.success("Güvenlik uyarı ayarları kaydedildi");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSavingCfg(false); }
  };

  const TYPES = [["", "Tümü"], ["login", "Giriş"], ["step_up", "Kimlik Doğrulama"], ["critical_submit", "Kritik İşlem"], ["impossible_travel", "İmkânsız Seyahat"], ["new_device", "Yeni Cihaz"]];
  const failCount = rows.filter((r) => r.success === false).length;
  const lockUser = async (r) => {
    if (!r.user_id) return;
    if (!window.confirm(`"${r.name || r.email}" hesabının TÜM oturumları hemen kapatılacak ve bir sonraki girişte şifre sıfırlaması zorunlu olacak.\n\n'Bu bendim değil' — onaylıyor musunuz?`)) return;
    try {
      await api.post(`/security/lock-user/${r.user_id}`);
      toast.success(`${r.email} kilitlendi — oturumlar kapatıldı, şifre sıfırlama zorunlu`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="security-page">
      <SecurityProofDashboard />
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-slate-900"><ShieldAlert className="h-7 w-7 text-brand-green" /> Güvenlik Denetim Günlüğü</h1>
        <p className="mt-1 text-sm text-slate-500">Girişler, kimlik doğrulamaları ve kritik işlem gönderimleri — kim, ne zaman, hangi IP / İl-Ülke ve cihazdan yaptı. <span className="font-semibold text-red-600">İmkânsız seyahat</span> ve başarısız kayıtlar kırmızı gösterilir.</p>
      </div>

      <div className="flex flex-wrap gap-2 text-xs" data-testid="security-summary">
        <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-600">Toplam: {rows.length}</span>
        <span className="rounded-full bg-red-50 px-3 py-1 font-semibold text-red-600">Başarısız: {failCount}</span>
        <span className="rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-700">Başarılı: {rows.length - failCount}</span>
      </div>

      {/* Aktif Oturumlar — gerçek zamanlı çevrimiçi kullanıcı (bakım penceresi / haftalık piyasa taraması için) */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="active-sessions-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-2 font-heading text-base font-bold text-slate-900">
            <Users className="h-4 w-4 text-brand-green" /> Aktif Oturumlar
            <span data-testid="active-sessions-count" className={`rounded-full px-2 py-0.5 text-xs font-bold ${sessions?.count ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
              {sessions ? `${sessions.count} çevrimiçi` : "…"}
            </span>
          </p>
          <span className="text-[11px] text-slate-400">Son {sessions?.window_min ?? 10} dk · 20 sn'de bir yenilenir{sessions?.quiescent ? " · bakım için uygun (sakin)" : ""}</span>
        </div>
        <p className="mt-1 text-xs text-slate-500">Ağır haftalık piyasa taraması yalnızca <b>kimse çevrimiçi değilken</b> (sakin) çalışır. Buradan kimin bağlı olduğunu görebilirsiniz.</p>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="active-sessions-list">
          {sessions && sessions.count === 0 && <span className="text-sm text-slate-400">Şu an çevrimiçi kullanıcı yok.</span>}
          {(sessions?.users || []).map((s) => (
            <div key={s.user_id} data-testid={`session-${s.email}`} className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 ring-1 ring-inset ring-slate-200">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-green/10 text-xs font-bold text-brand-green">{(s.name || "?").slice(0, 1).toLocaleUpperCase("tr")}</span>
              <div className="leading-tight">
                <p className="text-sm font-semibold text-slate-800">{s.name}</p>
                <p className="text-[10px] text-slate-400">{s.role || "—"} · {s.minutes_ago != null ? `${s.minutes_ago} dk önce` : "az önce"}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bakım Modu — tüm kullanıcılara üst bant uyarısı + ağır işleri sakin pencerede çalıştırma teşviki */}
      {maint && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="maintenance-card">
          <p className="flex items-center gap-2 font-heading text-base font-bold text-slate-900"><Wrench className="h-4 w-4 text-amber-600" /> Bakım Modu</p>
          <p className="mt-1 text-xs text-slate-500">Açıkken tüm kullanıcılara üst bantta bakım uyarısı gösterilir. Ağır işlemleri (haftalık piyasa taraması) tercihen kimse çevrimiçi değilken çalıştırın.</p>
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2.5 rounded-lg bg-slate-50 px-3 py-2.5">
              <Switch data-testid="maint-enabled" checked={maint.enabled} onCheckedChange={(v) => setMaint({ ...maint, enabled: v })} />
              <Label className="mb-0 text-sm font-semibold">{maint.enabled ? "Bakım modu AÇIK" : "Kapalı"}</Label>
            </div>
            <div className="min-w-[220px] flex-1">
              <Label className="text-xs">Mesaj (opsiyonel)</Label>
              <Input data-testid="maint-message" value={maint.message || ""} onChange={(e) => setMaint({ ...maint, message: e.target.value })} placeholder="Örn: 02:00-04:00 arası bakım yapılacaktır" className="mt-1" />
            </div>
            <Button data-testid="maint-save" onClick={() => saveMaint(maint)} className="bg-brand-green text-white hover:bg-brand-green-600"><Save className="mr-1.5 h-4 w-4" /> Kaydet</Button>
          </div>
          {maint.updated_at && <p className="mt-2 text-[11px] text-slate-400">Son güncelleme: {String(maint.updated_at).slice(0, 19).replace("T", " ")} · {maint.updated_by || ""}</p>}
        </div>
      )}

      {/* Otomatik Bakım Penceresi (Salı 05:00 → en geç 07:45) — durum + ayar + kuru test */}
      <MaintenanceWindowCard />

      {/* İmkânsız Seyahat eşik ayarları */}
      {cfg && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="security-config-card">
          <p className="flex items-center gap-2 font-heading text-base font-bold text-slate-900"><SlidersHorizontal className="h-4 w-4 text-brand-green" /> İmkânsız Seyahat (Impossible Travel) Ayarları</p>
          <p className="mt-1 text-xs text-slate-500">Kısa sürede coğrafi olarak çok uzak bir konumdan giriş = hesap ele geçirme şüphesi → Yönetici'ye anlık uyarı. Meşru hızlı VPN geçişleriniz yanlış alarm veriyorsa eşiği yükseltin.</p>
          <div className="mt-4 flex flex-wrap items-end gap-5">
            <div className="flex items-center gap-2.5 rounded-lg bg-slate-50 px-3 py-2.5">
              <Switch data-testid="sec-cfg-enabled" checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} />
              <Label className="mb-0 text-sm font-semibold">{cfg.enabled ? "Etkin" : "Devre dışı"}</Label>
            </div>
            <div>
              <Label className="text-xs">Min. mesafe (km)</Label>
              <Input data-testid="sec-cfg-min-km" type="number" min="50" value={cfg.min_km} onChange={(e) => setCfg({ ...cfg, min_km: e.target.value })} className="mt-1 w-28" disabled={!cfg.enabled} />
            </div>
            <div>
              <Label className="text-xs">Maks. hız (km/s)</Label>
              <Input data-testid="sec-cfg-max-kmh" type="number" min="100" value={cfg.max_kmh} onChange={(e) => setCfg({ ...cfg, max_kmh: e.target.value })} className="mt-1 w-28" disabled={!cfg.enabled} />
            </div>
            <Button data-testid="sec-cfg-save" onClick={saveCfg} disabled={savingCfg} className="bg-brand-green text-white hover:bg-brand-green-600">
              {savingCfg ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Kaydet
            </Button>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4" data-testid="alert-freq-row">
            <span className="text-sm font-semibold text-slate-700">Uyarı e-postası sıklığı:</span>
            {[["each", "Her olayda"], ["daily", "Günde 1 (önerilen)"], ["off", "Kapalı (sadece panelde)"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`alert-freq-${v}`} onClick={() => setAlertFreq(v)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${alertFreq === v ? "border-brand-green bg-brand-green text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{l}</button>
            ))}
            <span className="text-[11px] text-slate-400">İmkânsız seyahat + yeni cihaz uyarıları için geçerli. "Kaydet" ile uygulanır.</span>
          </div>
        </div>
      )}

      {/* Yeni Cihaz / Oturum Girişleri — kim, hangi cihaz/IP/konumdan ilk kez giriş yaptı */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="new-devices-card">
        <p className="flex items-center gap-2 font-heading text-base font-bold text-slate-900"><MonitorSmartphone className="h-4 w-4 text-amber-600" /> Yeni Cihaz Girişleri</p>
        <p className="mt-1 text-xs text-slate-500">Bir hesaba yeni bir cihaz/tarayıcıdan ilk giriş yapıldığında burada listelenir ve (sıklık ayarına göre) e-posta uyarısı gönderilir.</p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500">
              <tr><th className="px-3 py-2">İlk Görülme</th><th className="px-3 py-2">Kullanıcı</th><th className="px-3 py-2">Cihaz</th><th className="px-3 py-2">IP</th><th className="px-3 py-2">İl / Ülke</th><th className="px-3 py-2">Son Görülme</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {newDevices.length === 0
                ? <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">Kayıtlı cihaz yok.</td></tr>
                : newDevices.slice(0, 12).map((d) => (
                  <tr key={d.id} data-testid={`new-device-${d.id}`}>
                    <td className="whitespace-nowrap px-3 py-2 text-slate-500">{fmtDT(d.first_seen)}</td>
                    <td className="px-3 py-2"><div className="font-medium text-slate-800">{d.user_name || d.user_email || "—"}</div><div className="text-[11px] text-slate-400">{d.user_email}</div></td>
                    <td className="px-3 py-2 text-slate-600">{d.device_label || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{d.ip || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{[d.city, d.country].filter(Boolean).join(" / ") || "—"}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-slate-400">{fmtDT(d.last_seen)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {TYPES.map(([k, l]) => (
          <button key={k || "all"} data-testid={`sec-type-${k || "all"}`} onClick={() => setType(k)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${type === k ? "bg-brand-green text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"}`}>{l}</button>
        ))}
        <button data-testid="sec-only-failures" onClick={() => setOnlyFail((v) => !v)}
          className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${onlyFail ? "bg-red-600 text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"}`}>
          <AlertTriangle className="h-3.5 w-3.5" /> Sadece başarısız{failCount ? ` (${failCount})` : ""}
        </button>
        <Input data-testid="sec-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kullanıcı / IP / İl ara..." className="h-9 w-56" />
      </div>

      <div className="max-h-[560px] overflow-auto rounded-xl bg-white ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="px-3 py-2.5">Zaman</th>
              <th className="px-3 py-2.5">Kullanıcı</th>
              <th className="px-3 py-2.5">Olay</th>
              <th className="px-3 py-2.5">Yöntem / İşlem</th>
              <th className="px-3 py-2.5">IP</th>
              <th className="px-3 py-2.5">İl / Ülke</th>
              <th className="px-3 py-2.5">Cihaz</th>
              <th className="px-3 py-2.5 text-center">Sonuç</th>
              <th className="px-3 py-2.5 text-center">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-400">Yükleniyor...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-400">Kayıt yok.</td></tr>
            ) : secPaged.slice.map((r) => {
              const tm = TYPE_META[r.type] || { label: r.type, Icon: ShieldAlert, cls: "bg-slate-100 text-slate-600" };
              const fail = r.success === false;
              const hasGeo = r.lat != null && r.lon != null;
              return (
                <tr key={r.id} data-testid={`sec-row-${r.id}`} className={fail ? "bg-red-50/70" : ""}>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-500">{fmtDT(r.created_at)}</td>
                  <td className="px-3 py-2.5"><div className="font-medium text-slate-800">{r.name || r.email}</div><div className="text-[11px] text-slate-400">{r.email}</div></td>
                  <td className="px-3 py-2.5"><span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tm.cls}`}><tm.Icon className="h-3 w-3" /> {tm.label}</span></td>
                  <td className="px-3 py-2.5 text-slate-600">{r.type === "step_up" ? (METHOD_LABEL[r.method] || r.method || "—") : (r.action || "—")}</td>
                  <td className="px-3 py-2.5 font-mono text-[11px] text-slate-500">{r.ip || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-600">
                    <div className="flex items-center gap-1.5">
                      <span>{[r.city, r.country].filter(Boolean).join(" / ") || "—"}</span>
                      {hasGeo && (
                        <button data-testid={`sec-map-${r.id}`} onClick={() => setMapRow(r)} title="Haritada göster"
                          className="inline-flex items-center rounded-md p-1 text-brand-green transition-colors hover:bg-emerald-50">
                          <MapPin className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-[11px] text-slate-500">{r.device || "—"}</td>
                  <td className="px-3 py-2.5 text-center">
                    {fail
                      ? <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700">Başarısız</span>
                      : <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Başarılı</span>}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {r.user_id ? (
                      <button data-testid={`sec-lock-${r.id}`} onClick={() => lockUser(r)} title="Bu bendim değil — tüm oturumları kapat + şifre sıfırlamayı zorla"
                        className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-[11px] font-semibold text-rose-600 transition-colors hover:bg-rose-50">
                        <Lock className="h-3 w-3" /> Kilitle
                      </button>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination page={secPaged.page} pageSize={secPaged.pageSize} total={secPaged.total}
        onPage={secPaged.setPage} onPageSize={secPaged.setPageSize} testid="security-pagination" />

      <Dialog open={!!mapRow} onOpenChange={(o) => !o && setMapRow(null)}>
        <DialogContent className="max-w-lg" data-testid="sec-map-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-brand-green" /> Giriş Konumu</DialogTitle>
          </DialogHeader>
          {mapRow && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold text-slate-800">{[mapRow.city, mapRow.region, mapRow.country].filter(Boolean).join(", ") || "Bilinmeyen konum"}</span>
                <span className="font-mono text-[11px] text-slate-400">{mapRow.ip}</span>
              </div>
              <div className="overflow-hidden rounded-xl ring-1 ring-inset ring-slate-200">
                <iframe title="konum-haritasi" data-testid="sec-map-iframe" width="100%" height="300" frameBorder="0"
                  src={osmEmbed(mapRow.lat, mapRow.lon)} className="block" />
              </div>
              <a data-testid="sec-map-osm-link" href={`https://www.openstreetmap.org/?mlat=${mapRow.lat}&mlon=${mapRow.lon}#map=11/${mapRow.lat}/${mapRow.lon}`}
                target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-brand-green hover:underline">
                OpenStreetMap'te aç →
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
